import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Power, PowerOff, Trash2, X } from "lucide-react";
import {
  primaryContractsApi,
  type PrimaryContractType,
  type PrimaryEmploymentContract,
} from "@/features/finances/api/primaryContracts";
import { contractScansApi } from "@/features/finances/api/contractScans";
import { ContractScanField } from "@/features/finances/components/ContractScanField";
import { uploadErrorMessage } from "@/utils/managedFile";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatDate } from "@/utils/formatDate";

interface Props {
  employeeId: string;
  canManage: boolean;
  /** Scan upload/replace only — HR (kadrowa) has this without full canManage. */
  canManageScans?: boolean;
  employeeName: string;
}

const PRIMARY_QUERY_KEY = (employeeId: string) => ["primary-contracts", employeeId];

const TYPE_LABELS: Record<PrimaryContractType, string> = {
  uop: "Umowa o pracę",
  uz: "Umowa zlecenie",
  b2b: "B2B / Kontrakt",
};

interface FormState {
  contractType: PrimaryContractType;
  effectiveFrom: string;
  effectiveTo: string;
  note: string;
}

const EMPTY_FORM: FormState = {
  contractType: "uop",
  effectiveFrom: "",
  effectiveTo: "",
  note: "",
};

export function PrimaryEmploymentContractsPanel({
  employeeId,
  canManage,
  canManageScans = canManage,
  employeeName,
}: Props) {
  const qc = useQueryClient();
  // null = form closed; "new" = create; otherwise the id of the contract being edited.
  const [formTarget, setFormTarget] = useState<null | "new" | string>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const { data: contracts = [], isLoading } = useQuery({
    queryKey: PRIMARY_QUERY_KEY(employeeId),
    queryFn: () => primaryContractsApi.list(employeeId),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: PRIMARY_QUERY_KEY(employeeId) });
    // Activation syncs User.contract_type/contract_scan — refresh the roster.
    qc.invalidateQueries({ queryKey: ["users"] });
  };

  const onMutationError = (err: unknown) => {
    const detail =
      // @ts-expect-error axios response shape
      err?.response?.data?.errors?.detail ??
      // @ts-expect-error
      Object.values(err?.response?.data?.errors ?? {}).flat?.()?.[0] ??
      "Nie udało się zapisać umowy głównej.";
    setFormError(String(detail));
  };

  const closeForm = () => {
    setFormTarget(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const create = useMutation({
    mutationFn: () =>
      primaryContractsApi.create({
        employee: employeeId,
        contract_type: form.contractType,
        effective_from: form.effectiveFrom,
        effective_to: form.effectiveTo || null,
        note: form.note,
      }),
    onSuccess: () => {
      invalidate();
      closeForm();
    },
    onError: onMutationError,
  });

  const update = useMutation({
    mutationFn: (id: string) =>
      primaryContractsApi.update(id, {
        contract_type: form.contractType,
        effective_from: form.effectiveFrom,
        effective_to: form.effectiveTo || null,
        note: form.note,
      }),
    onSuccess: () => {
      invalidate();
      closeForm();
    },
    onError: onMutationError,
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      primaryContractsApi.update(id, { is_active: active }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => primaryContractsApi.remove(id),
    onSuccess: invalidate,
  });

  // Two-step: upload the PDF to /files/, then attach it to the contract.
  const uploadScan = useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const managedFile = await contractScansApi.uploadPdf(file);
      return primaryContractsApi.setScan(id, managedFile.id);
    },
    onSuccess: invalidate,
  });

  useEffect(() => {
    if (formTarget === null) setFormError(null);
  }, [formTarget]);

  const startEdit = (c: PrimaryEmploymentContract) => {
    setFormTarget(c.id);
    setForm({
      contractType: c.contract_type,
      effectiveFrom: c.effective_from,
      effectiveTo: c.effective_to ?? "",
      note: c.note ?? "",
    });
  };

  const saving = create.isPending || update.isPending;
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-[var(--color-text-primary)] uppercase tracking-wide">
            Umowa główna — historia
          </h4>
          <p className="text-xs text-[var(--color-text-tertiary)] mt-1">
            {employeeName ? `Pracownika ${employeeName}` : "Pracownika"}
            {" — tylko jedna umowa może być aktywna; aktywna wyznacza typ zatrudnienia."}
          </p>
        </div>
        {canManage && formTarget === null && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setForm(EMPTY_FORM);
              setFormTarget("new");
            }}
          >
            <Plus size={14} className="mr-1" /> Dodaj
          </Button>
        )}
      </div>

      {/* Form (create or edit) */}
      {canManage && formTarget !== null && (
        <div className="rounded-lg border border-[var(--color-border)] bg-white dark:bg-gray-800 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-medium text-[var(--color-text-primary)]">
              {formTarget === "new" ? "Nowa umowa główna" : "Edycja umowy głównej"}
            </h5>
            <button
              type="button"
              onClick={closeForm}
              className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]"
              aria-label="Zamknij"
            >
              <X size={16} />
            </button>
          </div>

          <div>
            <span className="text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wide block mb-1">
              Typ zatrudnienia<span className="text-[var(--color-primary)] ml-0.5">*</span>
            </span>
            <div className="flex rounded border border-[var(--color-border)] overflow-hidden w-fit">
              {(["uop", "uz", "b2b"] as const).map((ct, i) => (
                <button
                  key={ct}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, contractType: ct }))}
                  className={`px-3 py-1.5 text-sm transition-colors ${
                    i > 0 ? "border-l border-[var(--color-border)]" : ""
                  } ${
                    form.contractType === ct
                      ? "bg-[var(--color-primary)] text-white font-medium"
                      : "bg-white dark:bg-gray-800 text-[var(--color-text-secondary)] hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  {ct.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldDate
              label="Obowiązuje od"
              required
              value={form.effectiveFrom}
              onChange={(v) => setForm((f) => ({ ...f, effectiveFrom: v }))}
            />
            <FieldDate
              label="Obowiązuje do"
              hint="opcjonalnie"
              value={form.effectiveTo}
              onChange={(v) => setForm((f) => ({ ...f, effectiveTo: v }))}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wide block mb-1">
              Uwagi
            </label>
            <textarea
              className="w-full rounded border border-[var(--color-border)] bg-white dark:bg-gray-800 px-2 py-1.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] resize-none"
              rows={2}
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="np. aneks, okres próbny…"
            />
          </div>

          {form.effectiveTo && form.effectiveTo < today && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400">
              Data zakończenia już minęła — umowa zostanie zapisana jako
              nieaktywna (zakończona).
            </p>
          )}

          {formError && (
            <p className="text-xs text-red-600 dark:text-red-400">{formError}</p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" size="sm" onClick={closeForm}>
              Anuluj
            </Button>
            <Button
              type="button"
              size="sm"
              loading={saving}
              disabled={!form.effectiveFrom}
              onClick={() => {
                if (formTarget === "new") create.mutate();
                else if (formTarget) update.mutate(formTarget);
              }}
            >
              Zapisz umowę
            </Button>
          </div>
          {formTarget === "new" && (
            <p className="text-[11px] text-[var(--color-text-tertiary)]">
              Nowa umowa stanie się aktywna — poprzednia aktywna umowa zostanie
              automatycznie dezaktywowana (otwarta dostanie datę końca dzień przed
              początkiem nowej).
            </p>
          )}
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">Ładowanie…</p>
      ) : contracts.length === 0 ? (
        <p className="text-xs text-gray-400 dark:text-gray-500 italic">
          Brak umów głównych dla tego pracownika.
        </p>
      ) : (
        <div className="bg-white dark:bg-gray-800 overflow-x-auto">
          <table className="w-full text-xs min-w-[600px]">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Okres
                </th>
                <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Typ
                </th>
                <th className="text-center px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Skan
                </th>
                <th className="px-3 py-2 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {contracts.map((c) => (
                <ContractRow
                  key={c.id}
                  contract={c}
                  today={today}
                  canManage={canManage}
                  canManageScans={canManageScans}
                  togglePending={toggleActive.isPending}
                  removePending={remove.isPending}
                  scanUploading={
                    uploadScan.isPending && uploadScan.variables?.id === c.id
                  }
                  onScanSelect={(file) => uploadScan.mutate({ id: c.id, file })}
                  scanError={
                    uploadScan.isError && uploadScan.variables?.id === c.id
                      ? uploadErrorMessage(uploadScan.error, "Nie udało się wgrać skanu — spróbuj ponownie.")
                      : null
                  }
                  onEdit={() => startEdit(c)}
                  onToggleActive={() => {
                    if (c.is_active) {
                      if (
                        window.confirm(
                          "Dezaktywować tę umowę główną?\n\nPracownik pozostanie bez aktywnej umowy głównej do czasu aktywowania innej."
                        )
                      ) {
                        toggleActive.mutate({ id: c.id, active: false });
                      }
                    } else {
                      toggleActive.mutate({ id: c.id, active: true });
                    }
                  }}
                  onDelete={() => {
                    if (
                      window.confirm(
                        "Usunąć umowę główną z historii?\n\nDziałanie nieodwracalne."
                      )
                    ) {
                      remove.mutate(c.id);
                    }
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Row ───────────────────────────────────────────────────────────────────────

function ContractRow({
  contract,
  today,
  canManage,
  canManageScans,
  togglePending,
  removePending,
  scanUploading,
  onScanSelect,
  scanError,
  onEdit,
  onToggleActive,
  onDelete,
}: {
  contract: PrimaryEmploymentContract;
  today: string;
  canManage: boolean;
  canManageScans: boolean;
  togglePending: boolean;
  removePending: boolean;
  scanUploading: boolean;
  onScanSelect: (file: File) => void;
  scanError: string | null;
  onEdit: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
}) {
  const okresText = `${formatDate(contract.effective_from)} — ${formatDate(contract.effective_to, "∞")}`;
  // An ended contract cannot be (re)activated — expiry self-deactivates it.
  const isPast = contract.effective_to !== null && contract.effective_to < today;
  const status: "active" | "past" | "inactive" =
    isPast ? "past" : contract.is_active ? "active" : "inactive";

  const STATUS_CONFIG = {
    active: {
      label: "Aktywna",
      cls: "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800",
    },
    past: {
      label: "Zakończona",
      cls: "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600",
    },
    inactive: {
      label: "Nieaktywna",
      cls: "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600",
    },
  };
  const cfg = STATUS_CONFIG[status];

  return (
    <tr
      className={`transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/40 ${
        status === "active" ? "" : "opacity-60"
      }`}
      title={contract.note ? `Uwagi: ${contract.note}` : undefined}
    >
      <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{okresText}</td>
      <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
        <span className="font-medium">{contract.contract_type.toUpperCase()}</span>
        <span className="ml-1 text-gray-400 dark:text-gray-500">
          {TYPE_LABELS[contract.contract_type]}
        </span>
      </td>
      <td className="px-3 py-2 text-center">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${cfg.cls}`}
        >
          {cfg.label}
        </span>
      </td>
      <td className="px-3 py-2">
        <ContractScanField
          scan={
            contract.contract_scan
              ? {
                  url: contract.contract_scan_url,
                  original_filename:
                    contract.contract_scan_detail?.original_filename ?? "skan.pdf",
                }
              : null
          }
          canManage={canManageScans}
          uploading={scanUploading}
          onSelect={onScanSelect}
          uploadError={scanError}
          compact
        />
      </td>
      <td className="px-3 py-2">
        {canManage && (
          <div className="flex items-center justify-end gap-0.5">
            <button
              type="button"
              onClick={onEdit}
              title="Edytuj umowę"
              className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            >
              <Pencil size={14} />
            </button>
            <button
              type="button"
              onClick={onToggleActive}
              disabled={togglePending || (isPast && !contract.is_active)}
              title={
                contract.is_active
                  ? "Dezaktywuj umowę"
                  : isPast
                    ? "Umowa zakończona — zmień lub usuń datę końca, aby ją aktywować"
                    : "Aktywuj umowę"
              }
              className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200 disabled:opacity-50"
            >
              {contract.is_active ? <PowerOff size={14} /> : <Power size={14} />}
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={removePending}
              title="Usuń umowę"
              className="p-1 rounded text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 disabled:opacity-50"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

// ─── Form fields ───────────────────────────────────────────────────────────────

function FieldDate({
  label,
  required,
  hint,
  value,
  onChange,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wide">
        {label}
        {required && <span className="text-[var(--color-primary)] ml-0.5">*</span>}
        {hint && (
          <span className="ml-1 normal-case text-[var(--color-text-tertiary)] tracking-normal">
            ({hint})
          </span>
        )}
      </span>
      <DatePicker
        className="border-[var(--color-border)] text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
        value={value}
        onChange={onChange}
      />
    </label>
  );
}
